import React, { useCallback, useRef, useState } from 'react';
import {
  SaveButton,
  CREATE,
  UPDATE,
  fetchEnd,
  fetchStart,
  refreshView
} from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { connect } from 'react-redux';

import UserAddressForm from './UserAddressForm';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';

const UserAddressCreateUpdate = ({
  handleDialogToggle,
  operation,
  fetchStart,
  fetchEnd,
  refreshView,
  popupTitle,
  okButtonLabel,
  addressId,
  ...operationProps
}) => {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const notifyServerError = useNotifyServerError();

  const handleSaveClick = useCallback(() => {
    // Trigger a submit of our custom quick create/update form
    // This is needed because our modal action buttons are oustide the form
    formRef.current.submit();
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    formRef.current.reset();
    handleDialogToggle(state => !state);
  }, [handleDialogToggle, submitting]);

  const handleSubmit = useCallback(
    values => {
      // Dispatch an action letting react-admin know a API call is ongoing
      fetchStart();
      setSubmitting(true);

      // As we want to know when the new address has been created/updated in order to close the modal, we use the
      // dataProvider directly
      const crudOperation = operation === 'Create' ? CREATE : UPDATE;
      try {
        dataProvider(crudOperation, 'useraddresses', {
          data: {
            ...values,
            userId: operationProps.id
          }
        })
          .then(() => {
            setSubmitting(false);
            handleDialogToggle(state => !state);
            refreshView();
            setTimeout(() => formRef.current && formRef.current.reset());
            // Dispatch an action letting react-admin know a API call has ended
            fetchEnd();
          })
          .catch(error => {
            setSubmitting(false);
            notifyServerError(error);
            fetchEnd();
          });
      } catch (error) {
        setSubmitting(false);
        notifyServerError(error);
        fetchEnd();
      }
    },
    [
      fetchEnd,
      fetchStart,
      handleDialogToggle,
      operation,
      refreshView,
      notifyServerError,
      operationProps.id
    ]
  );

  return (
    <Dialog
      fullWidth
      open={true}
      aria-label={`${operation} user address`}
      maxWidth="sm"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        {popupTitle}
      </DialogTitleWithCloseButton>
      <DialogContent>
        <UserAddressForm
          {...operationProps}
          operation={operation}
          formRef={formRef}
          handleSubmit={handleSubmit}
          id={addressId}
        />
      </DialogContent>
      <DialogActions>
        <SaveButton
          style={{ minWidth: '82px' }}
          saving={submitting}
          handleSubmitWithRedirect={handleSaveClick}
          label={okButtonLabel}
          icon={<></>}
          id="add-button"
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          disabled={submitting}
          disableFocusRipple
          id="cancel-button"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  useraddresses: state.admin.resources.useraddresses, // List of user adresses
  useraddresstypes: state.admin.resources.useraddresstypes // List of user phone types
});

const mapDispatchToProps = {
  fetchEnd,
  fetchStart,
  refreshView
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserAddressCreateUpdate);
